import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import {
  Search,
  Home,
  LocationOn,
  AttachMoney,
  Category,
  Update,
  FilterList,
  TrendingUp,
  TrendingDown,
  Link as LinkIcon,
  OpenInNew
} from '@mui/icons-material';
import { API_URL } from './constants';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { predictPrice } from './api';

const RealEstateView = () => {
  const { projectKey } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterBuildingCondition, setFilterBuildingCondition] = useState('all');
  const [totalRows, setTotalRows] = useState(0);
  const [predictedPrices, setPredictedPrices] = useState({});
  const [predictionUncertainty, setPredictionUncertainty] = useState({}); // Store std_error and confidence intervals
  const [predictingIds, setPredictingIds] = useState(new Set());
  const [uniqueBuildingConditions, setUniqueBuildingConditions] = useState([]);

  useEffect(() => {
    fetchRealEstateData();
  }, [page, rowsPerPage, filterBuildingCondition]);
  
  // Load persisted price estimates when data changes
  useEffect(() => {
    if (data.length > 0 && columns.length > 0) {
      loadPriceEstimates();
    }
  }, [data, columns]);
  
  const loadPriceEstimates = async () => {
    try {
      const hashIdIdx = columns.findIndex(col => col.toLowerCase().includes('hash'));
      const idIdx = columns.findIndex(col => col.toLowerCase() === 'id');
      
      if (hashIdIdx < 0) return;
      
      // Load price estimates for all visible listings
      const estimates = {};
      const hashIds = data
        .map(row => {
          const hashId = hashIdIdx >= 0 ? String(row[hashIdIdx] || '') : '';
          const id = idIdx >= 0 ? row[idIdx] : null;
          return { hashId, listingId: id || hashId };
        })
        .filter(item => item.hashId);
      
      // Fetch estimates in batch (check cache for each)
      const uncertainty = {};
      for (const { hashId, listingId } of hashIds) {
        try {
          const response = await axios.get(`${API_URL}/real_estate/model/price-estimate/${hashId}`);
          if (response.data && response.data.predicted_price) {
            estimates[listingId] = response.data.predicted_price;
            if (response.data.std_error) {
              uncertainty[listingId] = {
                std_error: response.data.std_error,
                confidence_interval_68: response.data.confidence_interval_68,
                confidence_interval_95: response.data.confidence_interval_95,
                coefficient_of_variation: response.data.coefficient_of_variation
              };
            }
          }
        } catch (err) {
          // Not in cache, ignore
        }
      }
      
      setPredictedPrices(prev => ({ ...prev, ...estimates }));
      setPredictionUncertainty(prev => ({ ...prev, ...uncertainty }));
    } catch (err) {
      console.error('Error loading price estimates:', err);
    }
  };

  const fetchRealEstateData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = page * rowsPerPage;
      const limit = rowsPerPage;
      
      const params = {
        limit: limit,
        offset: offset
      };
      
      if (filterBuildingCondition && filterBuildingCondition !== 'all') {
        params.building_condition = filterBuildingCondition;
      }
      
      const response = await axios.get(`${API_URL}/real_estate/sreality/state`, { params });

      if (response.data) {
        setData(response.data.data || []);
        setColumns(response.data.columns || []);
        setTotalRows(response.data.total_rows || 0);
      }
    } catch (err) {
      console.error('Error fetching real estate data:', err);
      setError('Failed to load real estate data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePredictPrice = async (e, row, index) => {
    e.stopPropagation(); // Prevent row click navigation
    
    const idIndex = columns.findIndex(col => col.toLowerCase() === 'id');
    const hashIdIndex = columns.findIndex(col => col.toLowerCase().includes('hash'));
    const listingId = row[idIndex] || row[hashIdIndex] || index;
    
    // Check if already predicting
    if (predictingIds.has(listingId)) {
      return;
    }
    
    try {
      setPredictingIds(prev => new Set(prev).add(listingId));
      
      // Fetch detailed data for this listing
      const detailedResponse = await axios.get(`${API_URL}/real_estate/sreality/detailed-data`, {
        params: { limit: 1000, offset: 0 }
      });
      
      if (!detailedResponse.data || !detailedResponse.data.data) {
        throw new Error('Could not fetch detailed data');
      }
      
      // Find the specific listing
      const detailedColumns = detailedResponse.data.columns || [];
      const detailedData = detailedResponse.data.data || [];
      
      const idColIdx = detailedColumns.indexOf('id');
      const hashIdColIdx = detailedColumns.indexOf('hash_id');
      
      const listing = detailedData.find(drow => {
        const did = idColIdx >= 0 ? drow[idColIdx] : null;
        const dhashId = hashIdColIdx >= 0 ? drow[hashIdColIdx] : null;
        return String(did) === String(listingId) || String(dhashId) === String(listingId);
      });
      
      if (!listing) {
        throw new Error('Listing not found in detailed data');
      }
      
      // Extract features for prediction
      const addressIdx = detailedColumns.indexOf('address');
      const hashIdIdx = detailedColumns.indexOf('hash_id');
      const listingHashId = hashIdIdx >= 0 ? String(listing[hashIdIdx] || '') : '';
      const floorAreaIdx = detailedColumns.indexOf('floor_area');
      const usableAreaIdx = detailedColumns.indexOf('usable_area');
      const roomCountIdx = detailedColumns.indexOf('room_count_cb');
      const floorNumberIdx = detailedColumns.indexOf('floor_number');
      const floorsIdx = detailedColumns.indexOf('floors');
      const elevatorIdx = detailedColumns.indexOf('elevator');
      const garageIdx = detailedColumns.indexOf('garage');
      const parkingIdx = detailedColumns.indexOf('parking');
      const balconyIdx = detailedColumns.indexOf('balcony');
      const loggiaIdx = detailedColumns.indexOf('loggia');
      const terraceIdx = detailedColumns.indexOf('terrace');
      const cellarIdx = detailedColumns.indexOf('cellar');
      const buildingTypeIdx = detailedColumns.indexOf('building_type');
      const energyEffIdx = detailedColumns.indexOf('energy_efficiency_rating');
      
      const address = addressIdx >= 0 ? listing[addressIdx] : '';
      const hashId = hashIdIdx >= 0 ? String(listing[hashIdIdx]) : '';
      
      // Get coordinates from geocoding cache or geocode
      let latitude = null;
      let longitude = null;
      
      if (address && listingHashId) {
        try {
          // Try to get from cache first by checking if we can geocode
          const geocodeResponse = await axios.post(`${API_URL}/real_estate/enrich/bulk-geocode`, {
            listings: [{ hash_id: listingHashId, address: address }]
          });
          
          if (geocodeResponse.data && geocodeResponse.data.results && geocodeResponse.data.results[0]) {
            const result = geocodeResponse.data.results[0];
            if (result.found) {
              latitude = result.latitude;
              longitude = result.longitude;
            }
          }
        } catch (err) {
          console.error('Error geocoding for prediction:', err);
        }
      }
      
      if (!latitude || !longitude) {
        throw new Error('Could not get coordinates for this listing');
      }
      
      // Extract feature values
      const floorArea = floorAreaIdx >= 0 ? (listing[floorAreaIdx] || 0) : 0;
      const usableArea = usableAreaIdx >= 0 ? (listing[usableAreaIdx] || 0) : 0;
      
      // Parse room count
      let roomCount = 0;
      if (roomCountIdx >= 0) {
        const rc = listing[roomCountIdx];
        if (typeof rc === 'string') {
          const numbers = rc.match(/\d+/);
          roomCount = numbers ? parseFloat(numbers[0]) : 0;
        } else {
          roomCount = parseFloat(rc) || 0;
        }
      }
      
      const floorNumber = floorNumberIdx >= 0 ? (parseFloat(listing[floorNumberIdx]) || 0) : 0;
      const totalFloors = floorsIdx >= 0 ? (parseFloat(listing[floorsIdx]) || 0) : 0;
      
      // Binary features
      const hasElevator = elevatorIdx >= 0 ? (String(listing[elevatorIdx]).toLowerCase() === 'ano' || listing[elevatorIdx] === 1) : false;
      const hasGarage = garageIdx >= 0 ? (String(listing[garageIdx]).toLowerCase() === 'ano' || listing[garageIdx] === 1) : false;
      const hasParking = parkingIdx >= 0 ? (String(listing[parkingIdx]).toLowerCase() === 'ano' || listing[parkingIdx] === 1) : false;
      const hasBalcony = balconyIdx >= 0 ? (String(listing[balconyIdx]).toLowerCase() === 'ano' || listing[balconyIdx] === 1) : false;
      const hasLoggia = loggiaIdx >= 0 ? (String(listing[loggiaIdx]).toLowerCase() === 'ano' || listing[loggiaIdx] === 1) : false;
      const hasTerrace = terraceIdx >= 0 ? (String(listing[terraceIdx]).toLowerCase() === 'ano' || listing[terraceIdx] === 1) : false;
      const hasCellar = cellarIdx >= 0 ? (String(listing[cellarIdx]).toLowerCase() === 'ano' || listing[cellarIdx] === 1) : false;
      
      // Building type encoding
      let buildingTypeEncoded = 0;
      if (buildingTypeIdx >= 0) {
        const btype = String(listing[buildingTypeIdx] || '').toLowerCase();
        if (btype.includes('panel')) {
          buildingTypeEncoded = 1;
        } else if (btype.includes('cihla') || btype.includes('brick')) {
          buildingTypeEncoded = 2;
        } else if (btype.includes('beton') || btype.includes('concrete')) {
          buildingTypeEncoded = 3;
        }
      }
      
      // Energy efficiency
      let energyEff = 0;
      if (energyEffIdx >= 0) {
        const eff = String(listing[energyEffIdx] || '').toUpperCase();
        if (eff.includes('A')) energyEff = 7;
        else if (eff.includes('B')) energyEff = 6;
        else if (eff.includes('C')) energyEff = 5;
        else if (eff.includes('D')) energyEff = 4;
        else if (eff.includes('E')) energyEff = 3;
        else if (eff.includes('F')) energyEff = 2;
        else if (eff.includes('G')) energyEff = 1;
      }
      
      // Predict price (include hash_id for caching)
      const prediction = await predictPrice({
        hash_id: listingHashId,
        latitude,
        longitude,
        floor_area: parseFloat(floorArea) || 0,
        usable_area: parseFloat(usableArea) || 0,
        room_count: roomCount,
        floor_number: floorNumber,
        total_floors: totalFloors,
        has_elevator: hasElevator,
        has_garage: hasGarage,
        has_parking: hasParking,
        has_balcony: hasBalcony,
        has_loggia: hasLoggia,
        has_terrace: hasTerrace,
        has_cellar: hasCellar,
        building_type_encoded: buildingTypeEncoded,
        energy_efficiency: energyEff
      });
      
      setPredictedPrices(prev => ({
        ...prev,
        [listingId]: prediction.predicted_price
      }));
      
    } catch (error) {
      console.error('Error predicting price:', error);
      alert(`Failed to predict price: ${error.message || 'Unknown error'}`);
    } finally {
      setPredictingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(listingId);
        return newSet;
      });
    }
  };

  // Get unique values for filters - find columns by partial match
  const categoryColIdx = columns.findIndex(col => col.toLowerCase().includes('category'));
  const stateColIdx = columns.findIndex(col => col.toLowerCase().includes('state') && !col.toLowerCase().includes('building'));
  const typeColIdx = columns.findIndex(col => col.toLowerCase().includes('type') && !col.toLowerCase().includes('building'));
  
  const uniqueCategories = categoryColIdx >= 0 ? [...new Set(data.map(row => row[categoryColIdx]).filter(Boolean))] : [];
  const uniqueStates = stateColIdx >= 0 ? [...new Set(data.map(row => row[stateColIdx]).filter(Boolean))] : [];
  const uniqueTypes = typeColIdx >= 0 ? [...new Set(data.map(row => row[typeColIdx]).filter(Boolean))] : [];
  
  // Fetch unique building conditions from detailed data
  useEffect(() => {
    const fetchBuildingConditions = async () => {
      try {
        const response = await axios.get(`${API_URL}/real_estate/sreality/detailed-data`, {
          params: { limit: 1000, offset: 0 }
        });
        
        if (response.data && response.data.data) {
          const detailedColumns = response.data.columns || [];
          const detailedData = response.data.data || [];
          const buildingConditionIdx = detailedColumns.indexOf('building_condition');
          
          if (buildingConditionIdx >= 0) {
            const conditions = [...new Set(
              detailedData
                .map(row => row[buildingConditionIdx])
                .filter(Boolean)
                .map(String)
            )];
            setUniqueBuildingConditions(conditions);
          }
        }
      } catch (err) {
        console.error('Error fetching building conditions:', err);
      }
    };
    
    fetchBuildingConditions();
  }, []);

  // Filter data based on search and filters - find columns by partial match
  const filteredData = data.filter(row => {
    const addressIndex = columns.findIndex(col => col.toLowerCase().includes('address'));
    const categoryIndex = columns.findIndex(col => col.toLowerCase().includes('category'));
    const stateIndex = columns.findIndex(col => col.toLowerCase().includes('state') && !col.toLowerCase().includes('building'));
    const typeIndex = columns.findIndex(col => col.toLowerCase().includes('type') && !col.toLowerCase().includes('building'));
    
    const address = addressIndex >= 0 ? String(row[addressIndex] || '').toLowerCase() : '';
    const category = categoryIndex >= 0 ? row[categoryIndex] : '';
    const state = stateIndex >= 0 ? row[stateIndex] : '';
    const type = typeIndex >= 0 ? row[typeIndex] : '';
    
    const matchesSearch = !searchTerm || address.includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || category === filterCategory;
    const matchesState = filterState === 'all' || state === filterState;
    const matchesType = filterType === 'all' || type === filterType;
    
    return matchesSearch && matchesCategory && matchesState && matchesType;
  });

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return String(price);
  };

  const parsePriceValue = (priceValue) => {
    if (priceValue === null || priceValue === undefined) return null;
    
    // If it's already a number, return it
    if (typeof priceValue === 'number') {
      return isNaN(priceValue) || !isFinite(priceValue) ? null : priceValue;
    }
    
    // If it's a string, remove formatting and parse
    if (typeof priceValue === 'string') {
      let cleaned = priceValue.trim();
      
      // Handle European format (comma as decimal separator)
      // Check if comma is used as decimal separator (e.g., "1 234,56")
      // Look for pattern: digits, optional spaces, comma, digits (decimal part)
      const europeanDecimalMatch = cleaned.match(/(\d[\d\s]*),(\d+)/);
      if (europeanDecimalMatch) {
        // European format: replace comma with dot
        cleaned = cleaned.replace(/,/g, '.');
      } else {
        // If comma is used as thousands separator (e.g., "1,234,567"), remove it
        // Check if there are multiple commas or comma followed by 3 digits
        if (cleaned.match(/\d{1,3}(,\d{3})+/)) {
          cleaned = cleaned.replace(/,/g, '');
        }
      }
      
      // Remove all non-digit characters except decimal point and minus sign
      // But preserve the decimal point we just created
      cleaned = cleaned.replace(/[^\d.-]/g, '');
      
      // Remove any remaining spaces
      cleaned = cleaned.replace(/\s/g, '');
      
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) || !isFinite(parsed) ? null : parsed;
    }
    
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getStateColor = (state) => {
    switch (state?.toUpperCase()) {
      case 'NEW':
        return 'success';
      case 'UPDATED':
        return 'info';
      case 'DELETED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCategoryIcon = (category) => {
    return <Home />;
  };

  if (loading && data.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Home sx={{ fontSize: 32 }} />
          Real Estate Listings - Czech Republic
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Browse and search through real estate listings from sreality.cz
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Listings
              </Typography>
              <Typography variant="h4" component="div">
                {totalRows.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Filtered Results
              </Typography>
              <Typography variant="h4" component="div">
                {filteredData.length.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Categories
              </Typography>
              <Typography variant="h4" component="div">
                {uniqueCategories.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Current Page
              </Typography>
              <Typography variant="h4" component="div">
                {page + 1}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterList />
          <Typography variant="h6">Filters & Search</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Address"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.67}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {uniqueCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.67}>
            <FormControl fullWidth>
              <InputLabel>State</InputLabel>
              <Select
                value={filterState}
                label="State"
                onChange={(e) => setFilterState(e.target.value)}
              >
                <MenuItem value="all">All States</MenuItem>
                {uniqueStates.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.67}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                {uniqueTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.67}>
            <FormControl fullWidth>
              <InputLabel>Building Condition</InputLabel>
              <Select
                value={filterBuildingCondition}
                label="Building Condition"
                onChange={(e) => {
                  setFilterBuildingCondition(e.target.value);
                  setPage(0); // Reset to first page when filter changes
                }}
              >
                <MenuItem value="all">All Conditions</MenuItem>
                {uniqueBuildingConditions.map((condition) => (
                  <MenuItem key={condition} value={condition}>
                    {condition}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const columnLower = column.toLowerCase();
                return (
                  <TableCell
                    key={column}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      minWidth: columnLower.includes('address') ? 300 : 
                               columnLower.includes('url') ? 200 : 120
                    }}
                  >
                    {column.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                    ).join(' ')}
                  </TableCell>
                );
              })}
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  minWidth: 150
                }}
              >
                Predicted Price
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  minWidth: 150
                }}
              >
                Predicted Profit
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  minWidth: 200
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 3} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 3} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No listings found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row, index) => {
                const idIndex = columns.findIndex(col => col.toLowerCase() === 'id');
                const addressIndex = columns.findIndex(col => col.toLowerCase().includes('address'));
                const categoryIndex = columns.findIndex(col => col.toLowerCase().includes('category'));
                const priceIndex = columns.findIndex(col => col.toLowerCase().includes('price') && !col.toLowerCase().includes('predicted'));
                const stateIndex = columns.findIndex(col => col.toLowerCase().includes('state') && !col.toLowerCase().includes('building'));
                const typeIndex = columns.findIndex(col => col.toLowerCase().includes('type') && !col.toLowerCase().includes('building'));
                const updatedAtIndex = columns.findIndex(col => col.toLowerCase().includes('updated') || col.toLowerCase().includes('date'));
                const hashIdIndex = columns.findIndex(col => col.toLowerCase().includes('hash'));

                const listingId = row[idIndex] || row[hashIdIndex] || index;
                
                return (
                  <TableRow 
                    key={listingId} 
                    hover
                    onClick={() => navigate(`/${projectKey}/real_estate/${listingId}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {columns.map((column, colIndex) => {
                      const value = row[colIndex];
                      
                      const columnLower = column.toLowerCase();
                      
                      if (columnLower.includes('address')) {
                        return (
                          <TableCell key={column}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationOn fontSize="small" color="action" />
                              <Typography variant="body2">{value || 'N/A'}</Typography>
                            </Box>
                          </TableCell>
                        );
                      }
                      
                      if (columnLower.includes('category')) {
                        return (
                          <TableCell key={column}>
                            <Chip
                              icon={getCategoryIcon(value)}
                              label={value || 'N/A'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                        );
                      }
                      
                      if (columnLower.includes('price') && !columnLower.includes('predicted')) {
                        return (
                          <React.Fragment key={column}>
                            <TableCell key={column}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AttachMoney fontSize="small" color="success" />
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {formatPrice(value)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell key="predicted_price">
                              {predictedPrices[listingId] ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TrendingUp fontSize="small" sx={{ color: 'primary.main' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {new Intl.NumberFormat('cs-CZ', {
                                        style: 'currency',
                                        currency: 'CZK',
                                        minimumFractionDigits: 0
                                      }).format(predictedPrices[listingId])}
                                    </Typography>
                                  </Box>
                                  {predictionUncertainty[listingId] && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', ml: 2.5 }}>
                                      ±{new Intl.NumberFormat('cs-CZ', {
                                        style: 'currency',
                                        currency: 'CZK',
                                        minimumFractionDigits: 0
                                      }).format(predictionUncertainty[listingId].std_error)}
                                      {' '}({predictionUncertainty[listingId].coefficient_of_variation.toFixed(1)}%)
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {predictingIds.has(listingId) ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CircularProgress size={12} />
                                      <span>Predicting...</span>
                                    </Box>
                                  ) : (
                                    'N/A'
                                  )}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell key="predicted_profit">
                              {(() => {
                                const actualPriceRaw = priceIndex >= 0 ? row[priceIndex] : null;
                                const actualPrice = parsePriceValue(actualPriceRaw);
                                const predictedPrice = predictedPrices[listingId];
                                
                                if (actualPrice && actualPrice > 0 && predictedPrice && predictedPrice > 0) {
                                  const profit = predictedPrice - actualPrice;
                                  const profitPercent = (profit / actualPrice) * 100;
                                  const isProfit = profit > 0;
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {isProfit ? (
                                        <TrendingUp fontSize="small" sx={{ color: 'success.main' }} />
                                      ) : (
                                        <TrendingDown fontSize="small" sx={{ color: 'error.main' }} />
                                      )}
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 'bold',
                                          color: isProfit ? 'success.main' : 'error.main'
                                        }}
                                      >
                                        {isProfit ? '+' : ''}
                                        {new Intl.NumberFormat('cs-CZ', {
                                          style: 'currency',
                                          currency: 'CZK',
                                          minimumFractionDigits: 0
                                        }).format(profit)}
                                        {' '}
                                        ({isProfit ? '+' : ''}{profitPercent.toFixed(1)}%)
                                      </Typography>
                                    </Box>
                                  );
                                }
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    {predictedPrice ? 'N/A' : '-'}
                                  </Typography>
                                );
                              })()}
                            </TableCell>
                          </React.Fragment>
                        );
                      }
                      
                      if (columnLower.includes('state') && !columnLower.includes('building')) {
                        return (
                          <TableCell key={column}>
                            <Chip
                              label={value || 'N/A'}
                              size="small"
                              color={getStateColor(value)}
                            />
                          </TableCell>
                        );
                      }
                      
                      if (columnLower.includes('type') && !columnLower.includes('building')) {
                        return (
                          <TableCell key={column}>
                            <Chip
                              label={value || 'N/A'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        );
                      }
                      
                      if (columnLower.includes('updated') || columnLower.includes('date')) {
                        return (
                          <TableCell key={column}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Update fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(value)}
                              </Typography>
                            </Box>
                          </TableCell>
                        );
                      }
                      
                      if (columnLower.includes('hash')) {
                        return (
                          <TableCell key={column}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {value ? value.toString().slice(0, 10) + '...' : 'N/A'}
                            </Typography>
                          </TableCell>
                        );
                      }
                      
                      // Default rendering for all other columns
                      return (
                        <TableCell key={column}>
                          <Typography variant="body2">
                            {value !== null && value !== undefined ? String(value) : 'N/A'}
                          </Typography>
                        </TableCell>
                      );
                    })}
                    <TableCell key="actions" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 150 }}>
                        {predictingIds.has(listingId) ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                              Predicting...
                            </Typography>
                          </Box>
                        ) : predictedPrices[listingId] ? (
                          (() => {
                            const actualPriceRaw = priceIndex >= 0 ? row[priceIndex] : null;
                            const actualPrice = parsePriceValue(actualPriceRaw);
                            const predictedPrice = predictedPrices[listingId];
                            if (actualPrice && actualPrice > 0 && predictedPrice) {
                              const profit = predictedPrice - actualPrice;
                              const profitPercent = (profit / actualPrice) * 100;
                              const isProfit = profit > 0;
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {isProfit ? (
                                    <TrendingUp fontSize="small" sx={{ color: 'success.main' }} />
                                  ) : (
                                    <TrendingDown fontSize="small" sx={{ color: 'error.main' }} />
                                  )}
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 'bold',
                                      color: isProfit ? 'success.main' : 'error.main'
                                    }}
                                  >
                                    {isProfit ? '+' : ''}
                                    {new Intl.NumberFormat('cs-CZ', {
                                      style: 'currency',
                                      currency: 'CZK',
                                      minimumFractionDigits: 0
                                    }).format(profit)}
                                    {' '}
                                    ({isProfit ? '+' : ''}{profitPercent.toFixed(1)}%)
                                  </Typography>
                                </Box>
                              );
                            }
                            return (
                              <Typography variant="body2" color="text.secondary">
                                No price data
                              </Typography>
                            );
                          })()
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<TrendingUp />}
                            onClick={(e) => handlePredictPrice(e, row, index)}
                            sx={{ minWidth: 120 }}
                          >
                            Predict Price
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows per page:"
        />
      </TableContainer>

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredData.length} of {totalRows.toLocaleString()} total listings
          {searchTerm && ` (filtered by "${searchTerm}")`}
        </Typography>
      </Box>
    </Container>
  );
};

export default RealEstateView;

